// 2D asset manifest types and URL resolution (art §15.3). Pure: no DOM; `fetch` only inside loadManifest().
// public/assets/manifest.json is generated by scripts/optimize-art.mjs and is the ONLY data source: code never builds
// an asset path by hand. The DOM loader (`artImage()` / `inkGlyph()`) lives in src/ui/art/ArtImage.ts (F3) and calls
// artUrl() / inkHref() from here.
import type { AssetId } from './ids.gen';

export type { AssetId };

export type AssetCategory = 'icons/items' | 'icons/weapons' | 'icons/skills' | 'icons/prayers' | 'icons/pets'
  | 'icons/statuses' | 'icons/glosses' | 'icons/intents' | 'icons/currencies' | 'icons/verbs' | 'icons/whispers'
  | 'icons/forktags' | 'icons/keywords' | 'icons/gifts' | 'icons/day' | 'icons/places' | 'icons/rooms' | 'icons/shell'
  | 'icons/tempers' | 'cards' | 'portraits' | 'heraldry' | 'map' | 'scenes/arena' | 'scenes/stage' | 'scenes/crypt'
  | 'events/forks' | 'events/chronicle' | 'events/chapters' | 'ui/frames' | 'ui/drolleries' | 'ui/boot' | 'species'
  | 'letters'
  // extras beyond art §15.2's P0 table (owner request 2026-10-10): painted ornaments and seamless vellum tiles
  | 'ui/ornaments' | 'ui/textures';

/** [x, y, w, h] normalised to the cropped master. */
export type Rect01 = [x: number, y: number, w: number, h: number];

export interface AssetVariant { w: number; h?: number; bytes: number; q?: number; overBudget?: number }

export interface AssetEntry {
  id: AssetId;
  category: AssetCategory;
  family: 'painted' | 'inkOnly';
  /** cropped master size (aspect source of truth) */
  w: number; h: number;
  /** 'assets/icons/weapons/stockfish.3fa1c2d9' — append `.${w}.webp` */
  path: string;
  sha1: string;
  variants: AssetVariant[];
  alpha: boolean;
  ink?: { href: string; bytes: number };
  /** layout rects; F5 adds `logo` (blank cartouche for the DOM logo) and `seal` (painted seal under the DOM ring) */
  crops?: { square?: Rect01; window?: Rect01; bust?: Rect01; heroSlot?: Rect01; quiet?: Rect01; sky?: Rect01; logo?: Rect01; seal?: Rect01 };
  derived?: { square?: AssetVariant[] };
  /** 9-slice frames: slice size as a fraction of the side (0.25 → `border-image-slice: 25%`) */
  slice?: number;
  placeholder: 'card' | 'icon' | 'bust' | 'crest' | 'scene' | 'vignette' | 'frame' | 'doodle' | 'letter';
  dye?: string;
  altKey: string | null;
  loadTrigger: 'boot' | 'route' | 'idle' | 'demand';
  screens: string[];
  tier?: 'p0' | 'extra';
  kind?: string;
  gate: { paletteOk: boolean; inkOk: boolean; reviewed: boolean };
}

export interface AssetManifest { version: number; generatedAt: string; entries: AssetEntry[] }

/** Size rule (art §15.1): painted ≥ 28 CSS px; «Оттиск» 13–27 (intent roundels up to 32, rule 4). */
export const PAINTED_MIN_PX = 28;
export const INK_MIN_PX = 13;
export const INK_MAX_PX = 27;
export const INK_INTENT_MAX_PX = 32;

export class ArtSizeError extends Error {}

let current: { byId: Map<string, AssetEntry>; base: string } | null = null;
let devChecks = true;

/** Install a manifest (tests, SSR-less boot). `base` is prefixed to every path (default '/'). */
export function setManifest(m: AssetManifest, base = '/', opts: { dev?: boolean } = {}): void {
  current = { byId: new Map(m.entries.map((e) => [e.id, e])), base: base.endsWith('/') ? base : `${base}/` };
  if (opts.dev !== undefined) devChecks = opts.dev;
}

/** Fetch `/assets/manifest.json` once at boot (art §15.3: no-cache, in parallel with the fonts). */
export async function loadManifest(base = '/'): Promise<AssetManifest> {
  const res = await fetch(`${base.endsWith('/') ? base : `${base}/`}assets/manifest.json`, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`manifest ${res.status}`);
  const m = (await res.json()) as AssetManifest;
  setManifest(m, base);
  return m;
}

export function hasManifest(): boolean { return current !== null; }
export function assetEntry(id: AssetId | string): AssetEntry | undefined { return current?.byId.get(id); }
export function allAssets(): AssetEntry[] { return current ? [...current.byId.values()] : []; }

/**
 * The smallest variant ≥ displayCssPx × min(devicePixelRatio, dprCap), else the largest (art §15.3).
 * `displayCssPx` is the box's rendered width in CSS px. Pure: pass `dpr` explicitly in tests.
 * Throws ArtSizeError in dev when displayCssPx < 28 (size rule 1). Returns null for unknown or ink-only ids.
 */
export function artUrl(id: AssetId | string, displayCssPx: number, dprCap = 2, dpr = globalDpr(), saveData = globalSaveData()): string | null {
  if (devChecks && displayCssPx < PAINTED_MIN_PX) throw new ArtSizeError(`artUrl(${id}): ${displayCssPx}px < ${PAINTED_MIN_PX}px — use inkHref()`);
  const e = current?.byId.get(id);
  if (!e || !e.variants.length) return null;
  const cap = saveData ? Math.min(dprCap, 1.5) : dprCap;
  const need = displayCssPx * Math.min(dpr, cap);
  const sorted = [...e.variants].sort((a, b) => a.w - b.w);
  const pick = sorted.find((v) => v.w >= need) ?? sorted[sorted.length - 1];
  return `${current!.base}${e.path}.${pick.w}.webp`;
}

/** `srcset` with every variant (for <img srcset sizes>); null for unknown or ink-only ids. */
export function artSrcset(id: AssetId | string): string | null {
  const e = current?.byId.get(id);
  if (!e || !e.variants.length) return null;
  return [...e.variants].sort((a, b) => a.w - b.w).map((v) => `${current!.base}${e.path}.${v.w}.webp ${v.w}w`).join(', ');
}

/**
 * `<use href>` of the entry's «Оттиск» symbol. Throws ArtSizeError in dev outside 13–27 px (32 allowed for intent
 * roundels, rule 4). Returns null when the entry has no ink-cut.
 */
export function inkHref(id: AssetId | string, displayCssPx: number): string | null {
  const e = current?.byId.get(id);
  const max = e?.category === 'icons/intents' ? INK_INTENT_MAX_PX : INK_MAX_PX;
  if (devChecks && (displayCssPx < INK_MIN_PX || displayCssPx > max)) {
    throw new ArtSizeError(`inkHref(${id}): ${displayCssPx}px outside ${INK_MIN_PX}–${max}px — use artUrl()`);
  }
  if (!e?.ink) return null;
  return `${current!.base}${e.ink.href}`;
}

/** CSS `border-image` value for a painted 9-slice frame (`ui/frames/*`): `url(…) 25% fill / <width> / 0 round`. */
export function frameBorderImage(id: AssetId | string, borderCssPx: number, dprCap = 2, dpr = globalDpr()): string | null {
  const e = current?.byId.get(id);
  if (!e?.slice || !e.variants.length) return null;
  const need = (borderCssPx / e.slice) * Math.min(dpr, dprCap);
  const sorted = [...e.variants].sort((a, b) => a.w - b.w);
  const pick = sorted.find((v) => v.w >= need) ?? sorted[sorted.length - 1];
  return `url("${current!.base}${e.path}.${pick.w}.webp") ${Math.round(e.slice * 100)}% / ${borderCssPx}px / 0 round`;
}

function globalDpr(): number {
  const g = globalThis as { devicePixelRatio?: number };
  return typeof g.devicePixelRatio === 'number' && g.devicePixelRatio > 0 ? g.devicePixelRatio : 1;
}
function globalSaveData(): boolean {
  const n = (globalThis as { navigator?: { connection?: { saveData?: boolean } } }).navigator;
  return !!n?.connection?.saveData;
}
